import {
  applyGa4CartItemCategories,
  toGa4AddToCartItemPartial,
} from '@amblique/king-living/app/analytics/ga4/item-mapper';
import {roundGa4Currency} from '@amblique/king-living/app/analytics/ga4/currency';
import {
  getKingGuardOptionValue,
  parseSelectedOptionsUIPayload,
  resolveKingGuardSourceProduct,
} from '@amblique/king-living/app/utils/cart-utils';
import {getSelectedVariantProduct} from '@amblique/king-living/app/utils/customize-selections';

const KING_GUARD_ID = 'kingGuard';

export const KING_GUARD_GA4_ITEM_ID = KING_GUARD_ID;
export const KING_GUARD_DEFAULT_NAME = 'KingGuard';

const resolveKingGuardContext = (lineItem, masterProduct, productsById = {}) => {
  const productId = lineItem?.productId;

  if (!productId) {
    return {applicableProduct: null, kgSource: null};
  }

  const coverVariant = masterProduct ?
    getSelectedVariantProduct(masterProduct, productId) :
    null;
  const masterId = masterProduct?.id ?? masterProduct?.productId;
  const applicableProduct = coverVariant ??
    productsById[productId] ??
    (masterProduct && productId === masterId ? masterProduct : null);
  const kgSource = resolveKingGuardSourceProduct(
    masterProduct ?? applicableProduct,
    coverVariant ?? applicableProduct
  );

  return {applicableProduct, kgSource};
};

const isKingGuardIncluded = (payload, option, optionItem, applicableProduct) => {
  if (payload?.kingGuardFromSet) {
    return true;
  }

  if (applicableProduct?.c_kingGuardApplicable !== true) {
    return false;
  }

  if (!optionItem?.optionValueId || !option?.values?.length) {
    return false;
  }

  const noneValue = option.values.find((value) => value.price === 0 || value.price == null);
  const activeValue = option.values.find((value) => value.price > 0) || option.values[0];

  if (noneValue?.id && optionItem.optionValueId === noneValue.id) {
    return false;
  }

  return Boolean(activeValue?.id && optionItem.optionValueId === activeValue.id);
};

/**
 * Build KingGuard GA4 line item for cart add/remove.
 * @param {{ lineItem: object, masterProduct?: object|null, categoryProduct?: object|null, productsById?: Record<string, object>, useBasketPrice?: boolean }} params
 */
export const resolveKingGuardGa4Item = ({
  lineItem,
  masterProduct = null,
  categoryProduct = null,
  productsById = {},
  useBasketPrice = false,
}) => {
  if (!lineItem) {
    return null;
  }

  const category = masterProduct ?? categoryProduct;
  const payload = parseSelectedOptionsUIPayload(lineItem.c_selectedOptionsUI);
  const {applicableProduct, kgSource} = resolveKingGuardContext(lineItem, masterProduct, productsById);
  const option = kgSource?.options?.find((entry) => entry.id === KING_GUARD_ID);
  const optionItem = lineItem.optionItems?.find((entry) => entry.optionId === KING_GUARD_ID);

  if (!isKingGuardIncluded(payload, option, optionItem, applicableProduct)) {
    return null;
  }

  const activeValue = getKingGuardOptionValue(kgSource);
  const basketKgPrice = optionItem?.priceAfterItemDiscount ?? optionItem?.price;
  const price = useBasketPrice ?
    roundGa4Currency(
      basketKgPrice != null && Number.isFinite(Number(basketKgPrice)) ?
        Number(basketKgPrice) :
        (activeValue?.price ?? 0)
    ) ?? 0 :
    roundGa4Currency(activeValue?.price ?? 0) ?? 0;
  const itemName = useBasketPrice ?
    activeValue?.name || optionItem?.itemText || KING_GUARD_DEFAULT_NAME :
    activeValue?.name || KING_GUARD_DEFAULT_NAME;

  return applyGa4CartItemCategories(toGa4AddToCartItemPartial({
    itemId: KING_GUARD_GA4_ITEM_ID,
    itemName,
    price,
    quantity: 1,
  }), category);
};
